import {
    Permission,
    PermissionType,
    PhoenixTask,
    TaskObjectType,
    ShareBaseDocument
} from 'phoenix/domain';

import { 
    ActionService,
    TaskService,
} from 'phoenix/services';

import { TaskAction } from 'sharebase/domain';
import { TrackingService } from 'sharebase/services'
import { TaskFactory } from 'sharebase/factories';
import { Serializable } from 'util/domain';
import * as moment from 'moment';

const NullPhoenixTask: PhoenixTask = {
    TaskId: null,
    TaskType: null,
    TaskStatus: null,
    TaskResult: null,
    DateAssigned: null,
    DateCompleted: null,
    DateEnded: null,
    ObjectId:null,
    ObjectType:null,
    ObjectName:null,
    DueDate: null,
    Instructions: null,
    CreatorId: null,
    CreatorName: null,
    AssigneeId: null,
    AssigneeName: null,
    ExternalAssigneeEmail: null,
    Embedded: {
        Permissions: []
    },
    Links: {
        CompleteTaskUri: null,
        UpdateTaskUri: null
    }
}

export enum TaskResult {
    Positive = 'Positive',
    Negative = 'Negative'
}

export enum TaskStatus {
    Open = 'Open',
    Completed = 'Completed',
    Cancelled = 'Cancelled',
    Expired = 'Expired'
}

export enum TaskType {
    Approval = 'Approval'
}

export class Task extends Serializable<PhoenixTask>{
    public taskId: number;
    public assigneeId?: number;
    public assigneeName: string;
    public creatorId: number;
    public creatorName: string;
    public dateAssigned: Date;
    public dateEnded: Date;
    public dueDate: Date;
    public externalAssigneeEmail?: string;
    public instructions: string;
    public result: string;
    public status: string;
    public targetId: number;
    public targetObjectName: string;
    private targetObjectType: TaskObjectType;
    public type: string;
 
    public static readonly $inject: string[] = [
        'pxActionService',
    ];

    public get daysUntilDue() {
        let nowInMilliseconds = new Date(Date.now()).getTime();
        let dueDateInMilliseconds = this.dueDate.getTime();
        const millisecondsInOneDay = 1000*60*60*24;

        return (dueDateInMilliseconds - nowInMilliseconds)/(millisecondsInOneDay);
    }

    public get isCompleted() {
        return this.status === TaskStatus.Completed;
    }

    public get isOpen() {
        return this.status === TaskStatus.Open;
    }

    public get isCancelled() {
        return this.status === TaskStatus.Cancelled;
    }

    public get isExpired() {
        return this.status === TaskStatus.Expired;
    }

    public get isOverdue() {
        return new Date() > this.dueDate;
    }

    public get isDocumentShareExpired () {
        return this.isCancelled || this.isExpired || this.isDueOrCompletedFifteenDaysAgo();
    }

    constructor(
        pxTask: PhoenixTask = angular.copy(NullPhoenixTask),
        private pxActionService: ActionService,
        protected pxTaskService: TaskService
    ) {
        super(pxTask);
        this.resetToSource();
    }

    private resetToSource() {
        this.taskId = this.source.TaskId;
        this.assigneeId = this.source.AssigneeId;
        this.assigneeName = this.source.AssigneeName || this.source.ExternalAssigneeEmail;
        this.creatorId = this.source.CreatorId;
        this.creatorName = this.source.CreatorName;
        this.dateAssigned = new Date(this.source.DateAssigned);
        this.dateEnded = this.source.DateEnded && new Date(this.source.DateEnded) ||
                            this.source.DateCompleted && new Date(this.source.DateCompleted) ||
                            null;
        this.dueDate = new Date(this.source.DueDate);
        this.externalAssigneeEmail = this.source.ExternalAssigneeEmail;
        this.instructions = this.source.Instructions;
        this.result = this.source.TaskResult;
        this.status = this.source.TaskStatus;
        this.targetId = this.source.ObjectId;
        this.targetObjectName = this.source.ObjectName;
        this.targetObjectType = this.source.ObjectType;
        this.type = this.source.TaskType;
    }

    protected changeSource(pxTask: PhoenixTask) {
        this.source = pxTask;
        this.resetToSource();
    }

    public save(document: ShareBaseDocument){
        return this.pxTaskService.createTaskForDocument(this.toApi(), document)
            .then(pxTask => {
                this.changeSource(pxTask);
                this.emitTaskAction(TaskAction.CreatedTask);
            });
    }

    public cancel(){
        return this.pxTaskService.cancelTask(this.toApi())
            .then(pxTask => {
                this.changeSource(pxTask);
                this.emitTaskAction(TaskAction.CancelledTask);
            });
    }

    public complete(completionComment?: string){
        return this.pxTaskService.completeTask(this.toApi(), completionComment)
            .then(pxTask => {
                this.changeSource(pxTask);
                this.emitTaskAction(TaskAction.CompletedTask);
            });
    }

    public toApi() {
        // Time is not significant for dueDate, keeping only the date
        this.dueDate.setHours(0, 0, 0, 0);

        return {
            TaskId: this.taskId,
            TaskType: this.type,
            TaskStatus: this.status,
            TaskResult: this.result,
            DateAssigned: this.dateAssigned.toISOString(),
            DateEnded: this.dateEnded ? this.dateEnded.toISOString() : null,
            ObjectType: this.targetObjectType,
            ObjectId: this.targetId,
            ObjectName: this.targetObjectName,
            DueDate: this.dueDate.toISOString(),
            Instructions: this.instructions,
            CreatorId: this.creatorId,
            CreatorName: this.creatorName,
            AssigneeId: this.assigneeId,
            AssigneeName: this.assigneeName,
            ExternalAssigneeEmail: this.externalAssigneeEmail,
            Embedded: {
                Permissions: this.getPermissionsBasedOnTaskType()
            },
            Links: this.source.Links
        }
    }

    private getPermissionsBasedOnTaskType() {
        let requiredPermissions: Permission[] = [];
        let requiredPermissionTypes: PermissionType[];

        switch (this.type) {
            case TaskType.Approval:
                requiredPermissionTypes = [
                    PermissionType.READ
                ];
                break;

            default:
                requiredPermissionTypes = [
                    PermissionType.NONE
                ];
        }

        requiredPermissionTypes.forEach(permissionValue => {
            requiredPermissions.push({
                PermissionValue: permissionValue,
                Allow: true,
                Deny: false
            });
        });

        return requiredPermissions;
    }

    protected emitTaskAction(actionType: TaskAction){
        this.pxActionService.emit({
            type: actionType,
            payload: this
        });
    }

    private isDueOrCompletedFifteenDaysAgo() {
        let momentEnded = moment(this.dateEnded);
        return moment(new Date()).isAfter(momentEnded.add(15, 'd'));
    }
}

export class TrackableTask extends Task {
    public static readonly $inject: string[] = [
        'pxActionService',
        'sbTrackingService',
        'sbTaskFactory'
    ];

    constructor(
        pxTask: PhoenixTask = angular.copy(NullPhoenixTask),
        pxActionService: ActionService,
        private sbTrackingService: TrackingService,
        public sbTaskFactory: TaskFactory,
        pxTaskService: TaskService
    ) {
        super(pxTask,pxActionService, pxTaskService);
    }

    public save(document: ShareBaseDocument){
        return super.save(document)
            .then(() => {
                this.sbTaskFactory.trackTaskEvent('taskCreated');
            });
    }

    public complete(completionComment?: string){
        return super.complete(completionComment)
            .then(() => {
                this.sbTaskFactory.trackTaskEvent('taskCompleted');
            });
    }

}


// WEBPACK FOOTER //
// ./src/sharebase/domain/task.ts